import React from 'react';
import RolesGrid from './RolesGrid';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  HowToReg as HowToRegIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';

function RequestCard({ req, onReview, onReject, formatRelativeTime, showActions = true }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        p: 2,
        mb: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={1}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <EmailIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
            <Typography variant="body2" fontWeight={600} noWrap>
              {req.email}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0} ml={1}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" noWrap>
              {formatRelativeTime(req.createdAt || req.reviewedAt)}
            </Typography>
          </Box>
        </Box>

        {req.displayName && (
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {req.displayName}
            </Typography>
          </Box>
        )}

        {req.companyName && (
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {req.status === 'approved' ? req.resolution?.companyId || req.companyName : req.companyName}
            </Typography>
          </Box>
        )}

        {req.brands?.length > 0 && (
          <Box display="flex" gap={0.5} flexWrap="wrap" pt={0.5}>
            {req.brands.map((brand, i) => (
              <Chip key={i} label={brand} size="small" variant="outlined" />
            ))}
          </Box>
        )}

        {req.message && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontStyle: 'italic',
            }}
          >
            "{req.message}"
          </Typography>
        )}

        {req.status && !showActions && (
          <Box display="flex" alignItems="center" gap={1} pt={0.5}>
            <Chip
              label={req.status}
              color={req.status === 'approved' ? 'success' : 'error'}
              size="small"
            />
            {req.status === 'approved' && req.resolution?.companyCreated && (
              <Chip label="New company" size="small" variant="outlined" />
            )}
            {req.status === 'rejected' && req.rejectionReason && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                {req.rejectionReason}
              </Typography>
            )}
          </Box>
        )}

        {showActions && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                size="medium"
                startIcon={<CheckCircleIcon />}
                onClick={() => onReview(req)}
                fullWidth
                sx={{ py: 1.2 }}
              >
                Review
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="medium"
                startIcon={<CancelIcon />}
                onClick={() => onReject(req)}
                fullWidth
                sx={{ py: 1.2 }}
              >
                Reject
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
}

export default function SignupRequestManagerMobile({
  pendingRequests,
  historyRequests,
  companies,
  roles,
  loading,
  error,
  success,
  setError,
  tabValue,
  setTabValue,
  reviewDialog,
  reviewData,
  setReviewData,
  reviewErrors,
  setReviewErrors,
  submitting,
  rejectDialog,
  setRejectDialog,
  rejectReason,
  setRejectReason,
  handleOpenReview,
  handleCloseReview,
  handleApprove,
  handleRoleToggle,
  handleOpenReject,
  handleReject,
  getCompanyOptions,
  formatRelativeTime,
  CREATE_NEW_COMPANY,
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  const tabs = [
    { label: 'Pending', count: pendingRequests.length },
    { label: 'History', count: null },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <HowToRegIcon />
        <Typography variant="h6" fontWeight={700}>Signup Requests</Typography>
        {pendingRequests.length > 0 && (
          <Chip
            label={pendingRequests.length}
            color="primary"
            size="small"
            sx={{ fontWeight: 700 }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Tab switcher */}
      <Box
        display="flex"
        mb={3}
        sx={{
          borderRadius: 2,
          bgcolor: 'action.hover',
          p: 0.5,
          gap: 0.5,
        }}
      >
        {tabs.map((tab, i) => (
          <Button
            key={i}
            onClick={() => setTabValue(i)}
            fullWidth
            sx={{
              borderRadius: 1.5,
              py: 1,
              fontWeight: 600,
              fontSize: '0.875rem',
              bgcolor: tabValue === i ? 'background.paper' : 'transparent',
              color: tabValue === i ? 'primary.main' : 'text.secondary',
              boxShadow: tabValue === i ? 1 : 0,
              '&:hover': { bgcolor: tabValue === i ? 'background.paper' : 'action.selected' },
            }}
          >
            {tab.label}
            {tab.count !== null && tab.count > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  px: 0.75,
                  py: 0.1,
                  borderRadius: 10,
                  bgcolor: tabValue === i ? 'primary.main' : 'action.disabled',
                  color: tabValue === i ? 'primary.contrastText' : 'text.disabled',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  lineHeight: 1.6,
                }}
              >
                {tab.count}
              </Box>
            )}
          </Button>
        ))}
      </Box>

      {/* Pending tab */}
      {tabValue === 0 && (
        <>
          {pendingRequests.length === 0 ? (
            <Box textAlign="center" py={6}>
              <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No pending signup requests
              </Typography>
            </Box>
          ) : (
            pendingRequests.map((req) => (
              <RequestCard
                key={req.id}
                req={req}
                onReview={handleOpenReview}
                onReject={handleOpenReject}
                formatRelativeTime={formatRelativeTime}
                showActions
              />
            ))
          )}
        </>
      )}

      {/* History tab */}
      {tabValue === 1 && (
        <>
          {historyRequests.length === 0 ? (
            <Box textAlign="center" py={6}>
              <InboxIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No reviewed requests yet
              </Typography>
            </Box>
          ) : (
            historyRequests.map((req) => (
              <RequestCard
                key={req.id}
                req={req}
                formatRelativeTime={formatRelativeTime}
                showActions={false}
              />
            ))
          )}
        </>
      )}

      {/* Review Dialog — full screen on mobile */}
      <Dialog open={reviewDialog.open} onClose={handleCloseReview} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={700} component="span">Review Request</Typography>
          <IconButton onClick={handleCloseReview} edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={0.5}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">Applicant</Typography>
              <Typography variant="body1" fontWeight={600}>{reviewDialog.request?.email}</Typography>
              {reviewDialog.request?.companyName && (
                <Typography variant="body2" color="text.secondary">
                  Proposed company: {reviewDialog.request.companyName}
                </Typography>
              )}
              {reviewDialog.request?.message && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  "{reviewDialog.request.message}"
                </Typography>
              )}
            </Box>

            {reviewDialog.request?.brands?.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Brands</Typography>
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  {reviewDialog.request.brands.map((brand, i) => (
                    <Chip key={i} label={brand} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            <TextField
              fullWidth
              label="Display Name"
              value={reviewData.displayName}
              onChange={(e) => setReviewData({ ...reviewData, displayName: e.target.value })}
              margin="normal"
              size="medium"
            />

            <Autocomplete
              fullWidth
              options={getCompanyOptions()}
              getOptionLabel={(option) => option.name || ''}
              value={reviewData.company}
              onChange={(event, newValue) => {
                setReviewData({ ...reviewData, company: newValue });
                setReviewErrors({ ...reviewErrors, company: '' });
              }}
              onInputChange={(event, value, reason) => {
                if (reason === 'input') {
                  setReviewData((prev) => ({ ...prev, newCompanyName: value }));
                }
              }}
              filterOptions={(options, { inputValue }) => {
                const filtered = options.filter(
                  (opt) => !opt._isNew && opt.name.toLowerCase().includes(inputValue.toLowerCase())
                );
                if (inputValue && !options.find(
                  (opt) => !opt._isNew && opt.name.toLowerCase() === inputValue.toLowerCase()
                )) {
                  filtered.push({
                    id: CREATE_NEW_COMPANY,
                    name: `+ Create new company: ${inputValue}`,
                    _isNew: true,
                    _newName: inputValue,
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company"
                  margin="normal"
                  required
                  error={!!reviewErrors.company}
                  helperText={reviewErrors.company || `Proposed: "${reviewDialog.request?.companyName || ''}"`}
                  placeholder="Search or type to create"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />

            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ fontSize: '12px', fontWeight: 600, color: reviewErrors.roles ? 'error.main' : 'text.secondary', mb: 0.5 }}>
                Roles *
              </Box>
              {reviewErrors.roles && (
                <Box sx={{ fontSize: '11px', color: 'error.main', mb: 1 }}>{reviewErrors.roles}</Box>
              )}
              <RolesGrid
                allRoles={roles}
                selectedRoleIds={reviewData.roles}
                onToggle={handleRoleToggle}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
          <Button
            onClick={handleApprove}
            variant="contained"
            fullWidth
            size="large"
            disabled={submitting || !reviewData.company || !reviewData.roles.length}
            sx={{ py: 1.5 }}
          >
            {submitting ? <CircularProgress size={22} /> : 'Approve & Create User'}
          </Button>
          <Button
            color="error"
            variant="outlined"
            fullWidth
            size="large"
            sx={{ py: 1.5 }}
            onClick={() => {
              handleCloseReview();
              handleOpenReject(reviewDialog.request);
            }}
          >
            Reject Instead
          </Button>
          <Button onClick={handleCloseReview} fullWidth>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog — full screen on mobile */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, request: null })}
        fullScreen
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={700} component="span">Reject Request</Typography>
          <IconButton onClick={() => setRejectDialog({ open: false, request: null })} edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block">Rejecting request from</Typography>
            <Typography variant="body1" fontWeight={600}>{rejectDialog.request?.email}</Typography>
          </Box>
          <TextField
            fullWidth
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            rows={4}
            inputProps={{ maxLength: 500 }}
            helperText="Not sent to the user — for internal records only"
            placeholder="e.g. Unrecognized email domain, duplicate account…"
          />
        </DialogContent>

        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            fullWidth
            size="large"
            disabled={submitting}
            sx={{ py: 1.5 }}
          >
            {submitting ? <CircularProgress size={22} /> : 'Confirm Reject'}
          </Button>
          <Button
            onClick={() => setRejectDialog({ open: false, request: null })}
            fullWidth
            size="large"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
